import { toast } from 'react-toastify'
import { Bookmark, useSystemActions, useSystemState, useSystemStateActions } from '../../../modules/system'
import { BOOKMARK_LABELS } from '../bookmarkLabels'
import { MAX_BOOKMARKS } from '../types'

export const useBookmarkActions = () => {
    const { user } = useSystemState()
    const { setUser } = useSystemStateActions()
    const getSystemActions = useSystemActions()

    const bookmarks = user?.bookmarks || []
    const isMaxReached = bookmarks.length >= MAX_BOOKMARKS

    const isBookmarked = (routePath: string) =>
        bookmarks.some(b => b.routePath === routePath)

    const persistBookmarks = async (updatedBookmarks: Bookmark[], snapshot: typeof user) => {
        try {
            const { saveUser } = getSystemActions()
            await saveUser({ ...snapshot!, bookmarks: updatedBookmarks }, false)
        } catch {
            setUser(snapshot!)
            toast.error('Error al guardar el marcador. Intentá de nuevo.', {
                position: 'bottom-right',
                autoClose: 4000,
                hideProgressBar: true,
                theme: 'dark'
            })
        }
    }

    const addBookmark = async (routePath: string) => {
        if (!user || isMaxReached || isBookmarked(routePath)) return
        const snapshot = user
        const label = BOOKMARK_LABELS[routePath] || routePath
        const newBookmark: Bookmark = { routePath, label, order: bookmarks.length }
        const updatedBookmarks = [...bookmarks, newBookmark]
        setUser({ ...user, bookmarks: updatedBookmarks })
        await persistBookmarks(updatedBookmarks, snapshot)
    }

    const removeBookmark = async (routePath: string) => {
        if (!user) return
        const snapshot = user
        const updatedBookmarks = bookmarks
            .filter(b => b.routePath !== routePath)
            .map((b, index) => ({ ...b, order: index }))
        setUser({ ...user, bookmarks: updatedBookmarks })
        await persistBookmarks(updatedBookmarks, snapshot)
    }

    const reorderBookmarks = async (reordered: Bookmark[]) => {
        if (!user) return
        const snapshot = user
        const updatedBookmarks = reordered.map((b, index) => ({ ...b, order: index }))
        setUser({ ...user, bookmarks: updatedBookmarks })
        await persistBookmarks(updatedBookmarks, snapshot)
    }

    return {
        bookmarks,
        isMaxReached,
        isBookmarked,
        addBookmark,
        removeBookmark,
        reorderBookmarks
    }
}
